import dayjs from 'dayjs';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { ApiResponse, IApiQueryOptions } from 'api/ApiClient';
import { ApiQueryOperator, IAcceptancePaymentStats, IAction, IApiList, IApiQueryParam, IPayoutStats, ITipAcceptancePaymentSet } from 'types';
import { formatDateForApi } from 'utils/date';
import * as API from '../../api';
import * as Actions from './actions';
import * as ActionTypes from './types';

interface ISiteStatsPayload {
  siteId: string;
  startDate: string;
  endDate: string;
  options?: IApiQueryOptions;
}

export function* getSitePayoutStats({ payload }: IAction<ActionTypes.Stats, ISiteStatsPayload>) {
  try {
    const response: ApiResponse<IPayoutStats> = yield call(API.Sites.getPayoutsStats, payload.siteId, payload.startDate, payload.endDate, payload.options);
    yield put(Actions.getSitePayoutStatsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getSitePayoutStatsError(error));
  }
}

export function* getUserPayoutStats() {
  const startDate = formatDateForApi(dayjs(new Date()).subtract(29, 'day'));
  const last30DaysPaidQuery: IApiQueryParam[] = [
    { key: 'created_at', operator: ApiQueryOperator.gte, value: startDate },
    { key: 'status', operator: ApiQueryOperator.eq, value: 'paid' },
  ];
  const excludePaidStatsQuery: IApiQueryParam[] = [{ key: 'status', operator: ApiQueryOperator.neq, value: 'paid' }];

  try {
    const [last30DaysPaidResponse, payoutStatsResponse]: ApiResponse<IPayoutStats>[] = yield all([call(API.Users.listPayoutsStats, { q: last30DaysPaidQuery }), call(API.Users.listPayoutsStats, { q: excludePaidStatsQuery })]);

    payoutStatsResponse.body.byStatus.push({ ...last30DaysPaidResponse.body.byStatus[0] });

    yield put(Actions.getUserPayoutStatsSuccess(payoutStatsResponse.body));
  } catch (error: any) {
    yield put(Actions.getUserPayoutStatsError(error));
  }
}

export function* getEmployeePayoutStats({ payload }: IAction<ActionTypes.Stats, { id: string }>) {
  try {
    const response: ApiResponse<IPayoutStats> = yield call(API.Employees.listPayoutStats, payload.id);
    yield put(Actions.getEmployeePayoutStatsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getEmployeePayoutStatsError(error));
  }
}

export function* getTipAcceptancePaymentsPendingCount({ payload }: IAction<ActionTypes.Stats, { siteId: string }>) {
  try {
    const response: ApiResponse<IApiList<ITipAcceptancePaymentSet>> = yield call(API.Sites.getTipAcceptancePaymentsSets, payload.siteId, {
      q: [{ key: 'status', operator: ApiQueryOperator.eq, value: 'pending' }],
      limit: 1,
    });
    yield put(Actions.getAcceptancePaymentsPendingCountSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getAcceptancePaymentsPendingCountError(error));
  }
}

export function* getTipAcceptancePaymentStats({ payload }: IAction<ActionTypes.Stats, ISiteStatsPayload>) {
  try {
    const response: ApiResponse<IAcceptancePaymentStats> = yield call(API.Sites.getTipAcceptancePaymentStats, payload.siteId, payload.startDate, payload.endDate, payload.options);
    yield put(Actions.getAcceptancePaymentStatsSuccess(response.body));
  } catch (error: any) {
    yield put(Actions.getAcceptancePaymentStatsError(error));
  }
}

export function* watchGetSitePayoutStats() {
  yield takeEvery(ActionTypes.Stats.GET_SITE_PAYOUT_STATS_REQUEST, getSitePayoutStats);
}

export function* watchGetUserPayoutStats() {
  yield takeEvery(ActionTypes.Stats.GET_USER_PAYOUT_STATS_REQUEST, getUserPayoutStats);
}

export function* watchGetEmployeePayoutStats() {
  yield takeEvery(ActionTypes.Stats.GET_EMPLOYEE_PAYOUT_STATS_REQUEST, getEmployeePayoutStats);
}

export function* watchGetTipAcceptancePaymentsPendingCount() {
  yield takeEvery(ActionTypes.Stats.GET_TIP_ACCEPTANCE_PAYMENTS_PENDING_COUNT_REQUEST, getTipAcceptancePaymentsPendingCount);
}

export function* watchGetTipAcceptancePaymentStats() {
  yield takeEvery(ActionTypes.Stats.GET_TIP_ACCEPTANCE_PAYMENT_STATS_REQUEST, getTipAcceptancePaymentStats);
}

function* statsSaga() {
  yield all([fork(watchGetSitePayoutStats), fork(watchGetUserPayoutStats), fork(watchGetEmployeePayoutStats), fork(watchGetTipAcceptancePaymentsPendingCount), fork(watchGetTipAcceptancePaymentStats)]);
}
export { statsSaga };
